import { useEffect, useState } from 'react'

import { ApiError, api } from '../api'
import type {
  CoupleResponse,
  CoupleRhythmResponse,
  Gender,
  MeResponse,
  RhythmNeed,
  RhythmPersonDecade,
  TimeAccuracy,
} from '../../contracts/api'
import { CityPicker } from '../components/CityPicker'
import type { City } from '../components/CityPicker'
import { Collapsible } from '../components/Collapsible'
import { PairSeal } from '../components/PairSeal'
import { Bases } from '../components/Bases'
import { PairWave } from '../components/viz/PairWave'
import { haptic } from '../telegram'
import './we.css'

const ACCURACY: { id: TimeAccuracy; title: string }[] = [
  { id: 'exact', title: 'Точно' },
  { id: 'approx', title: 'Примерно' },
  { id: 'unknown', title: 'Не знаю' },
]

/** Подпись под потребностью: одна и та же нужда или разная скорость. */
const NEED_KIND: Record<RhythmNeed['kind'], string> = {
  'same-need-different-way': 'одна потребность, разные пути',
  'different-tempo': 'разный темп',
}

/** Что появится, когда второй человек будет заведён. Пустой экран обязан обещать конкретное. */
const PREVIEW: { title: string; note: string }[] = [
  { title: 'Ритмы сейчас', note: 'чей сегодня день и как тринадцать дней ложатся на обоих' },
  { title: 'Десятилетия рядом', note: 'в каком такте каждый и до какого года он держится' },
  { title: 'Одна потребность — разные способы', note: 'где вам нужно одно и то же, но разными путями' },
]

export function We(_props: { me: MeResponse }) {
  const [data, setData] = useState<CoupleResponse | null>(null)
  const [rhythm, setRhythm] = useState<CoupleRhythmResponse | null>(null)
  const [error, setError] = useState('')
  const [adding, setAdding] = useState(false)
  const [openTheme, setOpenTheme] = useState<string | null>('attraction')

  const [name, setName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [timeAccuracy, setTimeAccuracy] = useState<TimeAccuracy | null>(null)
  const [birthTime, setBirthTime] = useState('')
  const [city, setCity] = useState<City | null>(null)
  const [gender, setGender] = useState<Gender | null>(null)
  const [saving, setSaving] = useState(false)
  const [invite, setInvite] = useState<string | null>(null)

  function load(): void {
    api
      .get<CoupleResponse>('/api/couple')
      .then(setData)
      .catch((cause: unknown) => setError(cause instanceof ApiError ? cause.message : 'Не открылось'))
  }

  useEffect(load, [])

  // Ритмы грузим отдельно от пары: они считаются по обеим картам целиком и
  // нужны только когда второй человек уже есть. Отказ молчаливый — знак пары
  // и темы должны остаться на экране в любом случае.
  const coupleId = data?.couple?.id ?? null
  useEffect(() => {
    if (!coupleId) return undefined
    let cancelled = false
    api
      .get<{ rhythm: CoupleRhythmResponse | null }>('/api/couple/rhythm')
      .then((result) => {
        if (!cancelled) setRhythm(result.rhythm)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [coupleId])

  async function addPartner(): Promise<void> {
    if (!city || !timeAccuracy || !birthDate) return
    setSaving(true)
    setError('')
    try {
      await api.post('/api/couple/partner', {
        name,
        birthDate,
        birthTime: timeAccuracy === 'unknown' ? null : birthTime,
        timeAccuracy,
        cityId: city.id,
        gender,
      })
      haptic('medium')
      setAdding(false)
      setData(null)
      load()
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Не получилось добавить')
    } finally {
      setSaving(false)
    }
  }

  if (error && !data) return <div className="state">{error}</div>
  if (!data) return <div className="state">Смотрим, кто рядом…</div>

  if (adding) {
    const ready = Boolean(name && birthDate && city && timeAccuracy && (timeAccuracy === 'unknown' || birthTime))
    return (
      <div className="screen we">
        <div className="topbar">
          <div className="brand">АТЛАС</div>
          <div className="topnote">мы</div>
        </div>
        <h1 className="display">Второй человек</h1>
        <div className="sub">Данные нужны те же: без времени рождения часть карты не построится и у него.</div>

        <div className="field-group">
          <div className="lbl">Как его зовут</div>
          <input className="field" value={name} maxLength={60} onChange={(event) => setName(event.target.value)} />
        </div>
        <div className="field-group">
          <div className="lbl">Дата рождения</div>
          <input className="field" type="date" value={birthDate} onChange={(event) => setBirthDate(event.target.value)} />
        </div>
        <div className="field-group">
          <div className="lbl">Время рождения</div>
          <div className="choice-row">
            {ACCURACY.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`choice${timeAccuracy === option.id ? ' active' : ''}`}
                onClick={() => setTimeAccuracy(option.id)}
              >
                <span className="choice-title">{option.title}</span>
              </button>
            ))}
          </div>
          {timeAccuracy && timeAccuracy !== 'unknown' && (
            <input className="field" type="time" value={birthTime} onChange={(event) => setBirthTime(event.target.value)} />
          )}
        </div>
        <div className="field-group">
          <div className="lbl">Место рождения</div>
          <CityPicker value={city} onPick={setCity} />
        </div>
        <div className="field-group">
          <div className="lbl">Пол</div>
          <div className="choice-row">
            <button type="button" className={`choice${gender === 'female' ? ' active' : ''}`} onClick={() => setGender('female')}>
              <span className="choice-title">Женский</span>
            </button>
            <button type="button" className={`choice${gender === 'male' ? ' active' : ''}`} onClick={() => setGender('male')}>
              <span className="choice-title">Мужской</span>
            </button>
          </div>
        </div>

        <button type="button" className="btn btn-gold cta" disabled={!ready || saving} onClick={() => void addPartner()}>
          {saving ? 'Считаем…' : 'Собрать пару'}
        </button>
        <button type="button" className="btn btn-ghost cta" onClick={() => setAdding(false)}>
          Отмена
        </button>
        {error && <div className="field-warn">{error}</div>}
      </div>
    )
  }

  if (!data.couple) {
    return (
      <div className="screen we">
        <div className="topbar">
          <div className="brand">АТЛАС</div>
          <div className="topnote">мы</div>
        </div>
        <h1 className="display">Двое</h1>
        <div className="sub">
          Не процент совместимости, а то, как две структуры ведут себя рядом: где совпадаете, где расходитесь
          и чем закрываете друг друга.
        </div>
        <button type="button" className="btn btn-gold cta" onClick={() => setAdding(true)}>
          Ввести данные самому
        </button>
        <button
          type="button"
          className="btn btn-ghost cta"
          onClick={() => {
            void api
              .post<{ link: string }>('/api/couple/invite', {})
              .then((result) => setInvite(result.link))
              .catch(() => setError('Ссылка не создалась'))
          }}
        >
          Прислать приглашение
        </button>
        {invite && (
          <div className="invite">
            <div className="lbl">Приватная ссылка</div>
            <div className="invite-link">{invite}</div>
            <div className="invite-note">
              Второй человек откроет её, заведёт свои данные — и общий экран появится у обоих. Ссылка живёт трое суток.
            </div>
          </div>
        )}

        <div className="we-preview">
          <div className="lbl">Что появится</div>
          {PREVIEW.map((item) => (
            <div className="we-preview-row" key={item.title}>
              <div className="we-preview-title">{item.title}</div>
              <div className="we-preview-note">{item.note}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const couple = data.couple

  return (
    <div className="screen we">
      <div className="topbar">
        <div className="brand">АТЛАС</div>
        <div className="topnote">мы</div>
      </div>
      <h1 className="display we-title">Ты и {couple.name}</h1>

      <PairSeal firstGlyph={couple.cores.first} secondGlyph={couple.cores.second} secondName={couple.name} />

      <div className="lbl pairlbl" style={{ color: 'var(--gold)' }}>
        Знак вашей пары
      </div>
      {couple.chemistry && <div className="chem">{couple.chemistry}</div>}

      {couple.themes.map((theme) => {
        const open = openTheme === theme.id
        return (
          <div key={theme.id}>
            {open ? (
              <div className="card theme-open" style={{ boxShadow: 'inset 3px 0 0 var(--gold)' }}>
                <div className="lbl" style={{ color: 'var(--gold)' }}>
                  {theme.title}
                </div>
                <p>{theme.text}</p>
                <Bases items={theme.bases.slice(0, 2)} />
              </div>
            ) : (
              <button type="button" className="rowitem" onClick={() => setOpenTheme(theme.id)}>
                <div className="ttl">{theme.title}</div>
                <div className="right">›</div>
              </button>
            )}
          </div>
        )
      })}

      {rhythm && <Rhythm rhythm={rhythm} />}

      <button type="button" className="btn btn-gold cta" disabled>
        Полный отчёт пары
      </button>
      <div className="ctanote">интерактивная версия и PDF — по покупке, когда оплата включится</div>
    </div>
  )
}

/* ─── ритмы пары ───────────────────────────────────────────────────────── */

/**
 * Пара через циклы, а не через проценты: где вы сегодня, в каких тактах живёте
 * ближайшие годы и какие потребности у вас общие при разных способах их
 * закрывать. Три свёрнутые секции: разом это читать нельзя, а по одной — можно.
 */
function Rhythm({ rhythm }: { rhythm: CoupleRhythmResponse }) {
  const { partnerName, today, wave, decades, needs } = rhythm
  return (
    <div className="rowlist rhythm-sections">
      <Collapsible title="Ритмы сейчас" note="сегодня и ближайшие тринадцать дней" defaultOpen>
        <div className="card rhythm-card">
          <p className="rhythm-line">{today.line}</p>
          <Bases items={today.bases} />
        </div>
        <div className="viz-box rhythm-wave">
          <PairWave days={wave.days} partnerName={partnerName} />
        </div>
        <p className="rhythm-note">{wave.note}</p>
      </Collapsible>

      <Collapsible title="Десятилетия рядом" note="в каком такте каждый">
        <div className="rhythm-cols">
          <DecadeColumn name="ты" person={decades.first} />
          <DecadeColumn name={partnerName} person={decades.second} />
        </div>
        <p className="rhythm-note">{decades.note}</p>
      </Collapsible>

      <Collapsible title="Одна потребность — разные способы" note="одна нужда, разные пути">
        {needs.map((need) => (
          <NeedCard key={need.key} need={need} partnerName={partnerName} />
        ))}
        {needs.length === 0 && <p className="rhythm-note">Общих потребностей пока не набралось.</p>}
      </Collapsible>
    </div>
  )
}

function DecadeColumn({ name, person }: { name: string; person: RhythmPersonDecade }) {
  const head = [person.tactRu, person.tenGodRu, person.until ? `до ${person.until}` : null]
    .filter(Boolean)
    .join(' · ')
  return (
    <div className="rhythm-col">
      <div className="lbl">{name}</div>
      <div className="rhythm-col-head">{head || '—'}</div>
      {person.palaceRu && <div className="rhythm-col-palace">{person.palaceRu}</div>}
      {person.tenGodPeriod && <p className="rhythm-col-text">{person.tenGodPeriod}</p>}
      {person.palaceDecade && <p className="rhythm-col-text">{person.palaceDecade}</p>}
    </div>
  )
}

function NeedCard({ need, partnerName }: { need: RhythmNeed; partnerName: string }) {
  return (
    <div className="card rhythm-need">
      <div className="lbl">{NEED_KIND[need.kind]}</div>
      <p className="rhythm-need-title">{need.title}</p>
      <div className="rhythm-need-side">
        <span className="rhythm-need-who">ты</span>
        <span>{need.first.phrase}</span>
      </div>
      {need.first.evidence && <Bases items={[need.first.evidence]} />}
      <div className="rhythm-need-side">
        <span className="rhythm-need-who">{partnerName}</span>
        <span>{need.second.phrase}</span>
      </div>
      {need.second.evidence && <Bases items={[need.second.evidence]} />}
    </div>
  )
}
